import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { post } from '../../utils/api';
import { isInternalNoEmail, sanitizeEmail } from '../../utils/customerEmail';

const currencyFormat = (value, currency = 'USD') => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(parseFloat(value || 0));
  } catch {
    return `$${Number(value || 0).toFixed(2)}`;
  }
};

export default function OrderActionsDialog({ open, onClose, order, onActionComplete }) {
  const [refundAmount, setRefundAmount] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setRefundAmount('');
      setReason('');
      setError('');
      setMessage('');
    }
  }, [open]);

  if (!order) return null;

  const handleRefund = async (full = false) => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      const payload = full ? {} : { amount: refundAmount, reason };
      const res = await post(`/api/orders/${order.id}/refund`, payload);
      setMessage('Refund created successfully');
      onActionComplete && onActionComplete('refund', res);
    } catch (e) {
      setError(e?.message || 'Failed to create refund');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');
      const email = sanitizeEmail(order?.billing?.email || order?.billing_email || '');
      if (email && isInternalNoEmail(email)) {
        throw new Error("Cannot send receipt: this email is not customer's real email and was internally assigned.");
      }
      await post(`/api/orders/${order.id}/resend-receipt`, email ? { email } : {});
      setMessage('Receipt email sent');
      onActionComplete && onActionComplete('resend');
    } catch (e) {
      setError(e?.message || 'Failed to resend receipt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Order #{order.id}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Total: {currencyFormat(order.total, order.currency)} • Status: {order.status}
          </Typography>

          <Divider />

          <Typography variant="subtitle2">Refund</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <TextField
              label="Amount (leave blank for full)"
              size="small"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              sx={{ width: { xs: '100%', sm: 220 } }}
              disabled={loading}
            />
            <TextField
              label="Reason"
              size="small"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ flex: 1 }}
              disabled={loading}
            />
            <Button variant="contained" onClick={() => handleRefund(false)} disabled={loading}>Partial Refund</Button>
            <Button variant="outlined" onClick={() => handleRefund(true)} disabled={loading}>Full Refund</Button>
          </Stack>

          <Divider />

          <Typography variant="subtitle2">Receipt</Typography>
          <Button variant="outlined" onClick={handleResend} disabled={loading}>Resend Receipt</Button>

          {error && <Typography variant="body2" color="error">{error}</Typography>}
          {message && <Typography variant="body2" color="success.main">{message}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
